<script>
import { SancareOcticon } from '@sancare/typescript-commons'
import _ from 'lodash'

import PredictedDiagnosis from '../PredictedDiagnosis.vue'

export default {
  components: {
    'predicted-diagnosis': PredictedDiagnosis,
    'sancare-octicon': SancareOcticon,

  },
  props: {
    appSettings: { type: Object, required: true },
    rumId: { type: Number, required: true },
    type: { type: String, required: true },
    automationPreviewDiagnosisGroup: { type: Object, required: true },
    standardModeDiagnosisGroup: { type: Object, required: true },
    automationRulesPreview: { type: Object, default: null },
    healthData: { type: Object, required: true },
    selection: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    rumPredictedLabels: { type: Array, default: null },
    suggestionJustifications: { type: Object, default: null },
  },
  emits: ['make-selection'],
  data() {
    return {
      showMore: false,
    }
  },
  computed: {
    predictedDiagnoses() {
      return this.rumPredictedLabels.filter((dDiag) => dDiag.type === this.type)
    },
    isReconductionPreview() {
      // A rules automation group (reconduction) matched from the staylist we came from : the "Sancare"
      // card then shows a plain copy from the previous stay rather than a classic ML proposal, so it's
      // styled like an already-acquired coding (highlighted) instead of the usual proposal color block.
      return Boolean(this.automationRulesPreview && this.automationRulesPreview[`current${this.type}`])
    },
    currentDiagnosis() {
      // A rules automation group (reconduction) matched from the staylist we came from : show what it
      // would copy from the previous stay instead of the classic ML prediction. It has no justification,
      // since it's a plain copy rather than a detection.
      if (this.isReconductionPreview) {
        return {
          type: this.type,
          diagnosis: this.automationRulesPreview[`current${this.type}`],
          hints: [],
          chunks: [],
          canShowRational: false,
        }
      }

      // Retrieves the diagnosis that could be applied in automation mode
      if (!this.automationPreviewDiagnosisGroup[`current${this.type}`]) {
        return null
      }
      const currentReference = this.automationPreviewDiagnosisGroup[`current${this.type}`].reference
      const prediction = _.find(this.predictedDiagnoses, (dDiag) => dDiag.diagnosis.reference === currentReference)

      return {
        ...prediction,
        canShowRational: prediction.hasRational ?? _.some(prediction.hints, (h) => h.confidence >= this.appSettings['standard_mode_hint_threshold']),
      }
    },
    displayedDiagnoses() {
      // Retrieves all the predicted diagnoses except the favorite one (current automation diagnosis)
      let currentReference = null
      if (this.automationRulesPreview && this.automationRulesPreview[`current${this.type}`]) {
        currentReference = this.automationRulesPreview[`current${this.type}`].reference
      } else if (this.automationPreviewDiagnosisGroup[`current${this.type}`]) {
        currentReference = this.automationPreviewDiagnosisGroup[`current${this.type}`].reference
      }
      let existingReference = null
      if (this.standardModeDiagnosisGroup[`first${this.type}`]) {
        existingReference = this.standardModeDiagnosisGroup[`first${this.type}`].reference
      }

      // Creates a list with all the diagnoses that are not the main suggested diagnosis ('currentReference')
      // Orders those diagnoses by confidence and then reference
      // Computes two more info for each diagnosis :
      // - canShowRational : the suggested diagnosis must by displayable, with sufficient hint confidence (higher than standard mode threshold)
      // - isInAccordion : the diagnosis will be displayed only when showMore is active. It depends on whether the predicted diagnosis is the same as the one coded
      return _.map(
        _.filter(
          _.orderBy(this.predictedDiagnoses, ['confidence', 'reference'], ['desc', 'asc']),
          (dDiag) => dDiag.diagnosis.reference !== currentReference && (this.showMore || !dDiag.diagnosis.isInAccordion)
        ),
        (dDiag) => ({
          ...dDiag,
          // hasRational comes precomputed from the AI automation preview endpoint (no chunks/hints
          // shipped there); fall back to the legacy hints-based computation for other data sources.
          canShowRational: dDiag.hasRational ?? _.some(dDiag.hints, (h) => h.confidence >= this.appSettings['standard_mode_hint_threshold']),
          isInAccordion: dDiag.diagnosis.reference !== existingReference, // FIXME
        })
      )
    },
  },
  methods: {
    makeSelection(selection) {
      this.$emit('make-selection', selection)
    },
  }
}
</script>

<template>
  <div class="row g-0 gap-3 align-items-start">
    <!-- initial diagnosis -->
    <div class="col">
      <div class="card diagnosis-card highlighted">
        <div class="card-body">
          <h1 class="card-title">
            {{ type }} <small>- Établissement</small>
          </h1>
        </div>
        <div class="card-body">
          <div v-if="standardModeDiagnosisGroup[`first${type}`]">
            <h2 :title="standardModeDiagnosisGroup[`first${type}`].description">
              {{ standardModeDiagnosisGroup[`first${type}`].reference }}
            </h2>
            <div>{{ standardModeDiagnosisGroup[`first${type}`].description }}</div>
          </div>
          <h2 v-else>
            Pas de {{ type }}
          </h2>
        </div>
      </div>
    </div>
    <!-- predicted/manual diagnoses -->
    <div class="col">
      <div>
        <div
          class="card diagnosis-card"
          :class="{ highlighted: isReconductionPreview }"
        >
          <div class="card-body">
            <h1 class="card-title">
              {{ type }} <small>- Sancare</small>
            </h1>
          </div>
          <div
            v-if="currentDiagnosis"
            class="card-body diagnosis-title"
          >
            <predicted-diagnosis
              :can-show-rational="currentDiagnosis.canShowRational"
              :is-loading="isLoading"
              :selected="selection !== null && selection.diagType === type && selection.diagReference === currentDiagnosis.diagnosis.reference"
              :predicted-diagnosis="currentDiagnosis"
              :health-data="healthData"
              :suggestion-justifications="suggestionJustifications"
              @make-selection="makeSelection"
            >
              <div class="row g-0">
                <div class="col row g-0 align-items-center">
                  <h2
                    :title="currentDiagnosis.diagnosis.description"
                    class="col"
                  >
                    {{ currentDiagnosis.diagnosis.reference }}
                  </h2>
                  <div class="col-2">
                    <div
                      :class="{ selected: selection !== null && selection.diagType === type && selection.diagReference === currentDiagnosis.diagnosis.reference }"
                      class="btn-select-diagnosis icon-block"
                      @click.stop="makeSelection({ diagType: type, diagReference: currentDiagnosis.diagnosis.reference, savedSearchIds: [] })"
                    >
                      <sancare-octicon
                        name="note"
                        :width="18"
                        :height="18"
                      />
                    </div>
                  </div>
                  <p>{{ currentDiagnosis.diagnosis.description }}</p>
                </div>
              </div>
            </predicted-diagnosis>
          </div>
          <div
            v-else
            class="card-body diagnosis-title"
          >
            <h2>Pas de {{ type }}</h2>
          </div>
        </div>
        <div
          v-if="displayedDiagnoses.length"
          class="card diagnosis-card"
        >
          <div
            key="card-header"
            class="card-body"
          >
            <div>Autres suggestions :</div>
          </div>
          <predicted-diagnosis
            v-for="predictedDiagnosis in displayedDiagnoses"
            :key="predictedDiagnosis.diagnosis.reference"
            :can-show-rational="predictedDiagnosis.canShowRational"
            :is-loading="isLoading"
            :selected="selection !== null && selection.diagType === type && selection.diagReference === predictedDiagnosis.diagnosis.reference"
            :predicted-diagnosis="predictedDiagnosis"
            :health-data="healthData"
            :suggestion-justifications="suggestionJustifications"
            class="animated-height"
            @make-selection="makeSelection"
          >
            <div class="card-body diagnosis-title">
              <div class="row g-0 align-items-center">
                <div class="col">
                  <div class="row g-0 align-items-center">
                    <h2
                      :title="predictedDiagnosis.diagnosis.description"
                      class="col"
                    >
                      {{ predictedDiagnosis.diagnosis.reference }}
                    </h2>
                    <div class="col-2">
                      <div
                        :class="{ selected: selection !== null && selection.diagType === type && selection.diagReference === predictedDiagnosis.diagnosis.reference }"
                        class="btn-select-diagnosis icon-block"
                        @click.stop="makeSelection({ diagType: type, diagReference: predictedDiagnosis.diagnosis.reference, savedSearchIds: [] })"
                      >
                        <sancare-octicon
                          name="note"
                          :width="18"
                          :height="18"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </predicted-diagnosis>
          <div
            v-if="displayedDiagnoses.find(d => d.isInAccordion)"
            key="show-more"
            class="col-12 mb-1 py-0 text-center"
          >
            <span
              class="btn-more-diagnosis"
              @click="showMore = !showMore"
            >
              Afficher {{ showMore ? 'moins' : 'plus' }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-title {
  font-size: 2rem;
}

h2 {
  font-size: 1.1rem;
}
</style>
